import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { unwrapResult } from '@reduxjs/toolkit';
import * as React from 'react';
import { Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { DatabaseEntityContact, EntityUserAuthorization, deauthorizeEntityAuthorizationsByUser, getUserEntityAuthorizations } from '../../redux/ContactSlice';
import { downloadDocumentToBrowser } from '../../redux/DocumentsSlice';
import { isAdmin } from '../../redux/MiscSlice';




interface EntityListItem{
    code: string;
    name: string;
    enabled: boolean;
    checked: boolean;
}

interface entitySyncerProps {
    userEmailValue: string; 
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    entityCode: string;
}

const EntityDeauthorizationSyncer = (props: entitySyncerProps) => {

    const dispatch = useAppDispatch();

    const isAdminUser: boolean = useAppSelector(isAdmin);

    const [entityAuthOptions, setEntityAuthOptions] = React.useState<Array<EntityUserAuthorization>>([]);
    const [userAuthorizationListItems, setUserAuthorizationListItems] = React.useState<Array<EntityListItem>>([]);
    const [userLogin, setUserLogin] = React.useState<string>("");
    const [hasItemChecked, setHasItemChecked] = React.useState<boolean>(false);
    const [isLoadingList, setIsLoadingList] = React.useState<boolean>(false);
    const [selectAllIsChecked, setSelectAllIsChecked] = React.useState<boolean>(false);
    const [someOptionsEnabled, setSomeOptionsEnabled] = React.useState<boolean>(false);
    const [userEmail, setUserEmail] = React.useState<string>("");
    const [modalIsOpen, setModalIsOpen] = React.useState<boolean>(false);


    const toggleModal = () => {
        setModalIsOpen(!props.isOpen);
        props.setIsOpen(!props.isOpen);
    }

    const handleAuthorizationChanged = (checked: boolean, listItem: EntityListItem) => {
        const updatedItems = userAuthorizationListItems.map(item => item.code === listItem.code
                                ? { ...item, checked: checked } : item);
        setUserAuthorizationListItems(updatedItems);
        let atLeastOneChecked = updatedItems.some((item) => item.checked == true);

        if (hasItemChecked != atLeastOneChecked) {
            setHasItemChecked(atLeastOneChecked);
        }
    }

    const handleSaveChanges = () => {

        let changedAuthorizations = userAuthorizationListItems.filter((item) => item.checked == true);

        // Map the filtered items to an Array<DatabaseEntityContact>
        const databaseEntityContacts: Array<DatabaseEntityContact> = changedAuthorizations.map((auth) => ({
            entityCode: auth.code,
            email: userEmail,
            entityContactId: null,
            title: null,
            startDate: null,
            endDate: null,
            logerUserId: null,
            userName: null,
            firstName: null,
            lastName: null,
            contactInfoId: null,
            phoneNumber: null,
            extension: null,
            addressId: null,
            cityCode: null,
            street: null,
            pobox: null,
            zip: null,
            entityAuthorizations: [], // Leave this empty as it's not required
        }));

        dispatch(deauthorizeEntityAuthorizationsByUser(databaseEntityContacts)).then((result: any) => {

            const doc = unwrapResult(result);
            if (doc !== null) {
                downloadDocumentToBrowser(doc);
            }

            props.setIsOpen(false);
            clearHooks();
        });
    }

    const handleSelectAllCheckedChanged = (checked: boolean) => {

        if (checked != selectAllIsChecked) {
            setSelectAllIsChecked(checked);
            const updatedItems = userAuthorizationListItems.map(item => item.enabled == true ? {
                ...item,
                checked: checked
            } : item);

            setUserAuthorizationListItems(updatedItems);

            let atLeastOneChecked = updatedItems.some((item) => item.checked == true);

            if (hasItemChecked != atLeastOneChecked) {
                setHasItemChecked(atLeastOneChecked);
            }
        }
    }
  
    const clearHooks = () => {

        setUserLogin("");
        setEntityAuthOptions([]);
        setUserAuthorizationListItems([]);
        setUserEmail("");
        setSelectAllIsChecked(false);
    }

    React.useEffect(() => {

        if (props.isOpen == true) {
            if (isAdminUser == true) {
                setIsLoadingList(true);
                if (props.userEmailValue === "") {
                    setUserLogin("");
                    setEntityAuthOptions([]);
                    setUserAuthorizationListItems([]);
                } else {
                    setUserLogin(props.userEmailValue);

                    dispatch(getUserEntityAuthorizations(props.userEmailValue)).then((result: any) => {
                        var originalPromiseResult: Array<EntityUserAuthorization> | null = unwrapResult(result);

                        if (originalPromiseResult) {
                            setEntityAuthOptions(originalPromiseResult);
                        } else {
                            setEntityAuthOptions([]);
                        }
                    });
                }
                setIsLoadingList(false);
            }
        } else {
            clearHooks();
        }      
    }, [props.isOpen]); 

    React.useEffect(() => {

        if (props.userEmailValue.length > 0) {
            if (props.userEmailValue != userEmail) {
                setUserEmail(props.userEmailValue);
            }
        }

    }, [props.userEmailValue])

    React.useEffect(() => {

        let userAuthListItems: Array<EntityListItem> = [];

        var filteredEntityOptions = entityAuthOptions.filter((options) => options.entity.code != props.entityCode);

        const entitiesByCode = filteredEntityOptions.map((item) => item.entity);
        
        for (var i = 0; i < entitiesByCode.length; i++) {

            userAuthListItems.push({
                code: entitiesByCode[i].code,         
                name: entitiesByCode[i].name,
                enabled: true,
                checked: false
            });
                     
        }

        if (userAuthListItems.some((item: EntityListItem) => item.enabled == true)) {
            //sort by entity name alphabetically
            setUserAuthorizationListItems(userAuthListItems.sort((a: EntityListItem, b: EntityListItem) =>
            {
                const nameA = a.name.toLowerCase();
                const nameB = b.name.toLowerCase();
                if (nameA < nameB) {
                    return -1;
                }
                if (nameA > nameB) {
                    return 1;
                }
                return 0;
            }));
            setSomeOptionsEnabled(true);
            setModalIsOpen(true);
        } else {
            setModalIsOpen(false);
            setSomeOptionsEnabled(false);
            props.setIsOpen(false);
        }    

    }, [entityAuthOptions])

    const isAuthorizedRender = (
        <React.Fragment>

            <Modal isOpen={modalIsOpen} toggle={toggleModal} className="authorizationModal">
                <ModalHeader toggle={toggleModal} ></ModalHeader>
                <Row>
                    <Col md="1"></Col>
                    <Col md="10" style={{ textAlign: "center" }}>
                        <h2> User Associated With Multiple Entities!</h2>
                    </Col>
                    <Col md="1"></Col>
                </Row>
                <Row>
                    <Col md="1"></Col>
                    <Col md="10" style={{ textAlign: "center" }}>
                        <FontAwesomeIcon className="menuIcon" icon={faTriangleExclamation} style={{ color: "#ea0b0b", }} />
                    </Col>
                    <Col md="1"></Col>
                </Row>
                <Row>
                    <Col md="1"></Col>
                    <Col md="10">
                        <p>Select the checkbox next to each entity if you want the same change made to that account. <a href="#">{userLogin}</a> is associated with the following entities:</p>
                    </Col>
                    <Col md="1"></Col>
                </Row>
                <Row>
                    <Col md="1"></Col>
                    <Col md="10">
                        <Label checked>
                            <Input
                                type="checkbox"
                                checked={selectAllIsChecked}
                                disabled={someOptionsEnabled == false }
                                onChange={(e) => handleSelectAllCheckedChanged(e.target.checked)} /> {' Select All'}
                        </Label>
                    </Col>
                    <Col md="1"></Col>
                </Row>
                <ModalBody style={{ overflowY: "scroll" }}>
                    <Row>
                        <Col>
                        </Col>
                    </Row>
                    <Spinner children={''} style={{ width: '1rem', height: '1rem' }}
                        type="grow" color="warning"
                        hidden={isLoadingList == false} />

                    <div hidden={isLoadingList == true}>

                        <Row>
                            <Col>
                                {userAuthorizationListItems.map((item, index) => {
                                    
                                    return (
                                        <Row key={index} style={{
                                            backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#e9ecef',
                                        }}
                                            hidden={item.enabled == false}
                                        >
                                            <Col md="1" style={{ textAlign: "center" }}>

                                                <Label checked>
                                                    <Input
                                                        type="checkbox"
                                                        checked={item.checked}
                                                        disabled={item.enabled == false}
                                                        onChange={(e) => handleAuthorizationChanged(e.target.checked, item)} />
                                                </Label>

                                            </Col>
                                            <Col md="11">
                                                {' ' + item.name}
                                            </Col>
                                        </Row>
                                    );
                                })}
                            </Col>
                        </Row>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Row>
                        <Col md="5" style={{ textAlign: "left" }}>
                            <Button color="danger" onClick={() => { toggleModal(); setUserAuthorizationListItems([]); }}>Do not make changes to any other entities</Button>
                        </Col>
                        <Col md="5" style={{ textAlign: "right" }}>
                            <Button className="taxedit" disabled={hasItemChecked == false} onClick={() => { handleSaveChanges() } }>Make changes to all selected entities</Button>
                        </Col>
                    </Row>

                </ModalFooter>
            </Modal>
        </React.Fragment>
    );

    const isNotAuthorizedRender = (
        <></>
    );

    return (
        <React.Fragment>
            {isAdminUser == true && userLogin && userLogin != "" ? isAuthorizedRender : isNotAuthorizedRender}
        </React.Fragment>
    );

}


export default EntityDeauthorizationSyncer;






